import { Filter } from '../../components/catalogue/ItemCatalogue';
import { CurrencyState } from '../../contexts/CurrencyContext';
import { Item } from 'functions/src/types/firestore/Game/Collection/Item';
import { isRare } from '../rarityCheck';
import { getPackContent } from '../getPackContent';
const currencyMap = {
  1: 'ETH',
  43113: 'AVAX',
  137: 'MATIC',
  250: 'FTM',
};

export const convertPrice = (
  price: number,
  currency: CurrencyState,
  chainId: number,
): number => {
  const converted = price / 10 ** 18;
  return (
    converted *
    currency.currencyOptions.exchangeRates[currencyMap[`${chainId}`]]
  );
};

const chainMap = {
  Ethereum: 1,
  'Avalanche (Testnet)': 43113,
  Fantom: 250,
  Polygon: 137,
};

export const getFiltersMap = (
  filters: { [name: string]: Filter },
  currency: CurrencyState,
) => {
  return {
    chain: (item: Item) => {
      const supportedChains = filters.chain.values;
      return supportedChains.some((chain) => {
        return chainMap[`${chain}`] === item.contract.chainId;
      });
    },
    saleType: (item: Item) => {
      if (!item.saleType) {
        return true;
      }
      return filters.saleType.values.includes(item.saleType);
    },
    price: (item: Item) => {
      if (
        convertPrice(item?.buyPrice, currency, item.contract.chainId) <
        filters.price.values[0]
      ) {
        return false;
      }
      if (
        convertPrice(item?.buyPrice, currency, item.contract.chainId) >
        filters.price.values[1]
      ) {
        return false;
      }
      return true;
    },
    rareItemsOnly: (item: Item) => {
      return isRare(item.rarity as string);
    },
    saleDiscount: (item: Item) => {
      if (!item.discount) {
        return true;
      }
      if (item.discount < filters.saleDiscount[0]) {
        return false;
      }
      if (item.discount > filters.saleDiscount[1]) {
        return false;
      }
      return true;
    },
    collection: (item: Item) => {
      return filters.collection.values.includes(item.collectionTitle);
    },
    packs: (item: Item) => {
      return filters.packs.values
        .map((value) => (value as string).toLowerCase())
        .includes(item.title?.toLowerCase());
    },
    weapons: (item: Item) => {
      if (!item.attributes) {
        return false;
      }
      const { packItems } = getPackContent(item.attributes);
      const names = packItems.map((item) => item.name);
      return filters.weapons.values.every((itemName) => {
        return names.includes(itemName.toLowerCase());
      });
    },
    perks: (item: Item) => {
      if (!item.attributes) {
        return false;
      }
      const { packPerks } = getPackContent(item.attributes);
      if (!packPerks.length) {
        return false;
      }
      return filters.perks.values.every((perkName) => {
        return packPerks.includes(perkName.toLowerCase());
      });
    },
    skins: (item: Item) => {
      if (!item.attributes) {
        return false;
      }
      const { packItems } = getPackContent(item.attributes);
      const skins = packItems.map((item) => item.skin);
      return filters.skins.values.every((skinName) => {
        return skins.includes(skinName.toLowerCase());
      });
    },
    includeWhitelistItems: (item: Item, userId: string) => {
      return !item.whitelistUserIds || item.whitelistUserIds?.includes(userId);
    },
    includeRentedItems: (item: Item<Date>) => {
      const fulfillmentActive = item.fulfillmentsRecent?.find(
        ({ createdAt, expiresAt }) =>
          createdAt.getTime() <= Date.now() &&
          expiresAt.getTime() >= Date.now(),
      );
      const rentedUntil = fulfillmentActive?.expiresAt;
      return !rentedUntil || rentedUntil?.getTime() < Date.now();
    },
    tournamentRentalsOnly: (item: Item<Date>) => item.rentCompetitions,
  };
};
